// Teilnehmer eines Termins: interne Kollegen (users-Tabelle) + EXTERNE
// Salesforce-Kontakte. Der Picker in `pages/CalendarMs.tsx` speist damit alle
// drei Einstiege (Neuer Termin · Termin finden · Termin aus dieser Mail).
//
// Zwei Regeln, die hier bewusst zentral liegen:
//
//  1. **Intern/extern entscheidet die ADRESSE, nicht ein Flag.** Ein falsch
//     gesetztes Anzeige-Flag darf keinen Externen in den Verfügbarkeits-Pfad
//     schmuggeln — für Externe liefert Graph `getSchedule` keine belastbaren
//     Free/Busy-Daten (der email-service liest ein leeres/all-'0'-View als
//     „frei"). Der Server riegelt das zusätzlich ab (422
//     `external_attendees_unsupported`, app/routers/calendar.py).
//  2. **Nie mehr als 3 Treffer** (Outlook-Gefühl: kurze Liste, Enter übernimmt).
import type { Colleague } from "./recipientRank";

/** Haus-Domain — Spiegel von `_INTERNAL_SUFFIX` im Kalender-Router. */
export const INTERNAL_SUFFIX = "@bitop.de";

/** Kurze Trefferliste (Outlook-Verhalten) — harte Obergrenze. */
export const MAX_HITS = 3;

/** Ab hier läuft die Kontakt-Suche; darunter gar kein Request. */
export const MIN_CONTACT_QUERY = 2;

/** Debounce der Kontakt-Suche: eine SOQL pro Tastendruck ist nicht akzeptabel. */
export const CONTACT_DEBOUNCE_MS = 250;

/** Zeitschranke für den Prefill-Abgleich („Termin aus dieser Mail").
 *
 *  `fetch` hat KEIN eigenes Timeout (`api.ts::request` ist ein nacktes
 *  `fetch`): ein hängendes Salesforce — nicht antwortend, nicht fehlschlagend —
 *  ließe den „wird geprüft"-Zustand sonst UNBEGRENZT stehen und damit das
 *  Senden dauerhaft gesperrt. Nach Ablauf bricht der Aufrufer ab; der Abbruch
 *  landet im selben Zweig wie eine SF-Störung (Aussage offen, Senden frei).
 *  Der Ausweg „Chip entfernen" existiert zwar, kostet aber genau den Kunden,
 *  für den der Prefill gebaut wurde — er ist kein akzeptabler Terminalzustand. */
export const CONTACT_CHECK_TIMEOUT_MS = 10_000;

/** Antwort-Form von `GET /calendar/contacts?q=` (app/schemas/calendar.py). */
export interface ContactSuggestion {
  id: string;
  name: string;
  email: string;
  title?: string | null;
  account_name?: string | null;
  account_count?: number;
}

export function isInternalAddress(email: string): boolean {
  return (email || "").trim().toLowerCase().endsWith(INTERNAL_SUFFIX);
}

/** Dieselbe Adresse? Klein geschrieben + getrimmt verglichen.
 *
 *  Eigene Funktion, weil derselbe Vergleich an mehreren Stellen gebraucht wird
 *  und `a === b` auf rohen Adressen falsch ist: Graph und Salesforce liefern
 *  dieselbe Adresse in unterschiedlicher Schreibweise
 *  (`Wolfgang.Hansen@bitop.de` vs. `wolfgang.hansen@bitop.de`) — der Vergleich
 *  MUSS beides als gleich sehen, sonst rutscht die eigene Adresse als
 *  „anderer Mensch" durch. Genau diesen Fall fuehrt t9-verteiler-walk.mjs als
 *  `OWNER_MIXED` mit. */
export function isSameAddress(a: string, b: string): boolean {
  const x = (a || "").trim().toLowerCase();
  const y = (b || "").trim().toLowerCase();
  return x.length > 0 && x === y;
}

/** Die EIGENE Adresse aus einer Teilnehmer-Liste nehmen.
 *
 *  Man laedt sich nicht selbst ein: der Organisator ist man serverseitig ueber
 *  das Token (`user.owner_ref`), und Outlook fuehrt ihn ohnehin. Ein eigener
 *  Teilnehmer-Eintrag daneben ist keine zweite Zusage, sondern eine doppelte
 *  Zeile im Termin — und in der Slot-Suche ein Kalender, der gegen sich selbst
 *  geprueft wird.
 *
 *  `selfEmail == null` heisst „Adresse unbekannt" und filtert deshalb NICHTS:
 *  ohne Vergleichswert waere jede Entfernung geraten. */
export function withoutSelf<T extends { email: string }>(
  list: T[],
  selfEmail: string | null | undefined
): T[] {
  if (!selfEmail) return list;
  return list.filter((c) => !isSameAddress(c.email, selfEmail));
}

/** Sieht die Eingabe wie eine vollständige Adresse aus? KEINE Validierung —
 *  nur die Frage, ob „keine Treffer" als „diese ADRESSE steht nicht in
 *  Salesforce" erklärt werden soll statt als blankes „Keine Treffer.". */
export function looksLikeEmailAddress(q: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((q || "").trim());
}

/** Externer Teilnehmer = Adresse außerhalb des Hauses (Einladung geht raus). */
export function isExternalParticipant(c: Colleague): boolean {
  return !isInternalAddress(c.email);
}

/** Funktionspostfach (`empfang@`, `EF@`, …) statt Mensch — Mig 0098.
 *
 *  Die Unterscheidung kommt vom SERVER (`kind`), nicht aus einem Muster auf der
 *  Adresse: „sieht aus wie ein Postfach" ist geraten und würde `gf@bitop.de`
 *  (echte Person, Kürzel als Local-Part) falsch einsortieren. Nur was in
 *  `group_mailboxes` steht, ist eines. */
export function isFunctionMailbox(c: Colleague): boolean {
  return c.kind === "shared";
}

/** SF-Kontakt → Teilnehmer. `account`/`title` tragen die Firma sichtbar mit. */
export function toParticipant(c: ContactSuggestion): Colleague {
  return {
    name: c.name || c.email,
    email: c.email,
    department: null,
    account: c.account_name ?? null,
    accountCount: c.account_count ?? 1,
    title: c.title ?? null,
  };
}

/** Adressen, die in den `getSchedule`-Pfad dürfen (= ausschließlich interne). */
export function internalUpns(participants: Colleague[]): string[] {
  return participants.filter((p) => isInternalAddress(p.email)).map((p) => p.email);
}

export function externalParticipants(participants: Colleague[]): Colleague[] {
  return participants.filter(isExternalParticipant);
}

/** Interne + externe Treffer auf `MAX_HITS` mischen.
 *
 *  Kollegen sind der Alltagsfall und stehen oben — sobald es aber einen
 *  externen Treffer gibt, bleibt EIN Platz für ihn frei, damit ein gesuchter
 *  Kunde nicht hinter drei Kollegen verschwindet. */
export function mergeHits(
  internal: Colleague[],
  external: Colleague[]
): Colleague[] {
  const reserve = external.length > 0 ? 1 : 0;
  return [...internal.slice(0, Math.max(0, MAX_HITS - reserve)), ...external].slice(
    0,
    MAX_HITS
  );
}
